import React, { useState, useEffect, useMemo } from "react";
import { X, Search, ChevronLeft, ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const lineas = {
  "Línea Básica": ["Baron", "Amadeus", "Trendy", "Roly polyb"],
  "Línea Premium": [
    "Flyway", "Converse", "Carolina Herrera", "Miraflex", "Lacoste", "Guess",
    "Boss", "Nautica", "Calvin Klein", "Timberland", "Nike", "Nine West",
    "Tous", "Puma", "Playmobil", "Banana Republic"
  ]
};

const productos = Object.entries(lineas).flatMap(([linea, marcas]) =>
  marcas.map((marca, i) => {
    const nombreArchivo = marca.replace(/[\s.]+/g, "").toLowerCase();
    return {
      id: `${nombreArchivo}-${i}`,
      linea,
      marca,
      nombre: `${marca} Modelo A`, // SE MANTIENE PERO YA NO SE MUESTRA
      imagenes: [
        `/images/${nombreArchivo}1.jpg`,
        `/images/${nombreArchivo}2.jpg`
      ]
    };
  })
);

export default function Catalogo() {
  const [lineaSel, setLineaSel] = useState("Línea Básica");
  const [marcaSel, setMarcaSel] = useState("Todas");
  const [busqueda, setBusqueda] = useState("");
  const [modalProd, setModalProd] = useState(null);

  const [carouselIndex, setCarouselIndex] = useState({});
  const [fadeState, setFadeState] = useState({});

  useEffect(() => {
    const intervalo = setInterval(() => {
      setFadeState(() => {
        const next = {};
        productos.forEach((p) => (next[p.id] = true));
        return next;
      });

      const timeout = setTimeout(() => {
        setCarouselIndex((prev) => {
          const next = { ...prev };
          productos.forEach((p) => {
            next[p.id] = ((next[p.id] || 0) + 1) % p.imagenes.length;
          });
          return next;
        });

        setFadeState(() => {
          const next = {};
          productos.forEach((p) => (next[p.id] = false));
          return next;
        });
      }, 300);

      return () => clearTimeout(timeout);
    }, 2500);

    return () => clearInterval(intervalo);
  }, []);

  const marcasDisponibles = useMemo(
    () => ["Todas", ...lineas[lineaSel]],
    [lineaSel]
  );

  const productosFiltrados = productos.filter((p) => {
    const coincideLinea = p.linea === lineaSel;
    const coincideMarca = marcaSel === "Todas" || p.marca === marcaSel;
    const coincideTexto = p.marca.toLowerCase().includes(busqueda.toLowerCase());
    return coincideLinea && coincideMarca && coincideTexto;
  });

  return (
    <section id="catalogo" className="py-20 bg-gray-50 border-t">
      <div className="max-w-6xl mx-auto px-6">
        <h2 className="text-4xl font-bold text-center text-blue-900 mb-8">
          Catálogo de Productos
        </h2>

        {/* Filtros */}
        <div className="grid md:grid-cols-3 gap-4 mb-10">
          <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-xl shadow-md border">
            <Search size={18} className="text-gray-500" />
            <input
              type="text"
              placeholder="Buscar..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              className="w-full outline-none text-gray-700"
            />
          </div>

          <select
            className="px-4 py-2 rounded-xl border shadow bg-white cursor-pointer"
            value={lineaSel}
            onChange={(e) => {
              setLineaSel(e.target.value);
              setMarcaSel("Todas");
            }}
          >
            {Object.keys(lineas).map((l) => (
              <option key={l}>{l}</option>
            ))}
          </select>

          <select
            className="px-4 py-2 rounded-xl border shadow bg-white cursor-pointer"
            value={marcaSel}
            onChange={(e) => setMarcaSel(e.target.value)}
          >
            {marcasDisponibles.map((m) => (
              <option key={m}>{m}</option>
            ))}
          </select>
        </div>

        {/* Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {productosFiltrados.map((prod) => (
            <article
              key={prod.id}
              onClick={() => setModalProd(prod)}
              className="bg-white rounded-2xl border shadow hover:shadow-xl cursor-pointer transition transform hover:-translate-y-1"
            >
              <div className="h-48 overflow-hidden relative">
                <img
                  src={prod.imagenes[carouselIndex[prod.id] || 0]}
                  alt={prod.marca}
                  className={`w-full h-full object-cover transition-opacity duration-500 ${
                    fadeState[prod.id] ? "opacity-0" : "opacity-100"
                  }`}
                />
              </div>

              <div className="p-4">
                <h3 className="text-lg font-semibold text-blue-900 text-center">
                  {prod.marca}
                </h3>
              </div>
            </article>
          ))}
        </div>
      </div>

      {modalProd && (
        <CatalogoModal producto={modalProd} cerrar={() => setModalProd(null)} />
      )}
    </section>
  );
}

function CatalogoModal({ producto, cerrar }) {
  const { imagenes, marca } = producto;
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);

  useEffect(() => {
    const iv = setInterval(() => {
      setDirection(1);
      setIndex((i) => (i + 1) % imagenes.length);
    }, 3000);
    return () => clearInterval(iv);
  }, [imagenes.length]);

  const next = () => {
    setDirection(1);
    setIndex((i) => (i + 1) % imagenes.length);
  };

  const prev = () => {
    setDirection(-1);
    setIndex((i) => (i - 1 + imagenes.length) % imagenes.length);
  };

  const variants = {
    enter: (dir) => ({ x: dir > 0 ? 120 : -120, opacity: 0 }),
    center: { x: 0, opacity: 1, transition: { duration: 0.6 } },
    exit: (dir) => ({
      x: dir > 0 ? -120 : 120,
      opacity: 0,
      transition: { duration: 0.6 }
    })
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70" onClick={cerrar} />

      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-3xl p-5">
        <button
          onClick={cerrar}
          className="absolute right-4 top-4 text-gray-700 hover:text-black"
        >
          <X size={22} />
        </button>

        {/* ✅ SOLO LA MARCA - MODELO ELIMINADO */}
        <h3 className="text-2xl font-bold text-center mb-3">
          {marca}
        </h3>

        <div className="relative w-full h-80 overflow-hidden rounded-lg bg-white">
          <AnimatePresence initial={false} custom={direction}>
            <motion.img
              key={index}
              src={imagenes[index]}
              custom={direction}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              className="absolute inset-0 w-full h-full object-contain"
            />
          </AnimatePresence>

          <button
            onClick={prev}
            className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/80 rounded-full p-2 shadow"
          >
            <ChevronLeft size={20} />
          </button>

          <button
            onClick={next}
            className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/80 rounded-full p-2 shadow"
          >
            <ChevronRight size={20} />
          </button>
        </div>
      </div>
    </div>
  );
}

